import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { upperFirst } from 'lodash';
import { stringify } from 'query-string';
import { useEffect } from 'react';
import { useHistory } from 'react-router';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useChargeQueries from '@dashboard/hooks/queries/useChargeQueries';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderActions from '@dashboard/components/CardHeaderActions';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import EmptyState from '@dashboard/components/EmptyState';
import ExportView from '@dashboard/components/ExportView';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import SortInput from '@dashboard/components/Form/components/SortInput';
import PaymentItem from '@dashboard/components/PaymentItem';
import Spinner from '@dashboard/components/Spinner';
import PageFilterInput from '@dashboard/components/Form/components/PageFilterInput';
import {
  SORT_OPTIONS,
  FILTER_OPTIONS,
  getSortOptionFromQuery,
  getStatusFromQuery,
} from '@dashboard/lib/chargesConstants';

const PaymentsCharges = ({
  title = 'Payments',
  baseFilters = {},
  exportLabel = 'payments',
  hidePageFilter = false,
  filterOptions = FILTER_OPTIONS,
  hasCustomStatusFilter = false,
}) => {
  const query = useUrlQuery();
  const history = useHistory();
  const [sortOption, setSortOption] = useState(
    getSortOptionFromQuery(query.sortBy, query.sortDirection),
  );
  const [search, setSearch] = useState(query.search || '');
  const [status, setStatus] = useState(getStatusFromQuery(query.status));
  const [page, setPage] = useState(query.page || undefined);

  useEffect(() => {
    setSearch(query.search || '');
  }, [query.search]);

  useEffect(() => {
    setSortOption(getSortOptionFromQuery(query.sortBy, query.sortDirection));
  }, [query.sortBy, query.sortDirection]);

  useEffect(() => {
    setStatus(getStatusFromQuery(query.status));
  }, [query.status]);

  useEffect(() => {
    setPage(query.page || undefined);
  }, [query.page]);

  const sortBy = SORT_OPTIONS[sortOption].value;
  const sortDirection = SORT_OPTIONS[sortOption].direction;

  const sort = {
    [sortBy]: sortDirection,
  };

  const filter = {
    ...baseFilters,
    ...(FILTER_OPTIONS[status].value !== 'all' && {
      status: FILTER_OPTIONS[status].value,
    }),
    pageType: 'checkout',
    livemode: true,
    page: page || undefined,
  };

  useEffect(() => {
    history.replace({
      search: stringify({
        ...query,
        sortBy: sortBy,
        sortDirection: sortDirection,
        status: FILTER_OPTIONS[status].value,
        search: search,
        page: page || undefined,
      }),
    });
  }, [sortBy, sortDirection, status, search, page]);

  return (
    <Card variant="root">
      <CardHeader variant="root">
        {!hidePageFilter && <CardHeaderTitle variant="root">{title}</CardHeaderTitle>}

        <div className="w-full flex flex-col lg:flex-row gap-x-4 gap-y-2 items-center lg:justify-between">
          <div className="w-full max-w-96">
            {!hidePageFilter && (
              <PageFilterInput
                name="page"
                pageTypes={['checkout']}
                productTypes={['charge']}
                onChange={setPage}
                initialValue={query.page}
                placeholder="Filter by checkout"
              />
            )}
            {hidePageFilter && (
              <div className="leading-tight font-bold text-gray-900 pt-[2px] text-xl">{title}</div>
            )}
          </div>

          <CardHeaderActions>
            <SearchInput
              onChange={setSearch}
              name="search_payments"
              placeholder="Search by email or order ID"
              initialValue={search}
            />

            <FilterInput options={filterOptions} onChange={setStatus} value={status} />

            <SortInput options={SORT_OPTIONS} onChange={setSortOption} value={sortOption} />

            <div>
              <ExportView
                collection="payments"
                label={exportLabel}
                filters={{
                  ...filter,
                  sort: JSON.stringify(sort),
                  search,
                }}
              />
            </div>
          </CardHeaderActions>
        </div>
      </CardHeader>

      <CardContent className="overflow-auto mb-20">
        <CardListItem variant="header">
          <CardListItemElement size="3xl">Customer</CardListItemElement>
          <CardListItemElement size="large">Amount</CardListItemElement>
          <CardListItemElement size="4xl">Page</CardListItemElement>
          <CardListItemElement size="xl" align="right">
            Date
          </CardListItemElement>
          <CardListItemElement size="large">Abandonment</CardListItemElement>
          <CardListItemElement size="medium">Status</CardListItemElement>
        </CardListItem>

        <PaymentsChargesList
          sort={sort}
          search={search}
          filter={filter}
          hasCustomStatusFilter={hasCustomStatusFilter}
        />
      </CardContent>
    </Card>
  );
};

PaymentsCharges.propTypes = {
  title: PropTypes.string,
  baseFilters: PropTypes.object,
  exportLabel: PropTypes.string,
  hidePageFilter: PropTypes.bool,
  filterOptions: PropTypes.object,
  hasCustomStatusFilter: PropTypes.bool,
};

PaymentsCharges.defaultProps = {
  title: 'Payments',
  baseFilters: {},
  exportLabel: 'payments',
  hidePageFilter: false,
  filterOptions: FILTER_OPTIONS,
  hasCustomStatusFilter: false,
};

export function PaymentsChargesList({
  pageType,
  sort,
  search,
  filter = {},
  options = {},
  refreshKey,
  infinite = true,
  hasCustomStatusFilter = false,
}) {
  const { sellerId } = useAuth();

  const { getInfiniteCharges } = useChargeQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: ['charges', sellerId, sort, search, filter, refreshKey],
    queryFn: async ({ pageParam }) =>
      await getInfiniteCharges(sellerId, {
        ...filter,
        search,
        limit: 10,
        skip: pageParam * 10,
        sort: JSON.stringify(sort),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!infinite) return undefined;

      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  let collection = pageType === 'event' ? 'bookings' : 'payments';

  if (isLoading) {
    return <Spinner className="m-4" />;
  }

  if (isError) {
    return (
      <div className="m-4">Unable to show {collection}. Please refresh or contact support.</div>
    );
  }

  const isEmpty = !data?.pages?.[0]?.data?.length;

  if (isEmpty && search) {
    return (
      <EmptyState>
        <EmptyState.Title>No {collection} found</EmptyState.Title>
        <EmptyState.Subtitle>There aren't any results for this filter.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  if (isEmpty) {
    if (filter.status && !hasCustomStatusFilter) {
      return (
        <EmptyState>
          <EmptyState.Title>No {collection} yet</EmptyState.Title>
          <EmptyState.Subtitle>
            {upperFirst(filter.status).replace(/_/g, ' ')} {collection} will show up here.
          </EmptyState.Subtitle>
        </EmptyState>
      );
    }

    return (
      <EmptyState>
        <EmptyState.Title>No {collection} yet</EmptyState.Title>
        <EmptyState.Subtitle>Your {collection} will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  const hasFetchedAllPages = data?.pages?.[data?.pages?.length - 1]?.has_more === false;

  return (
    <>
      <BottomScrollListener onBottom={fetchNextPage}>
        {data?.pages?.map(data =>
          data?.data.map(payment => (
            <PaymentItem payment={payment} key={payment._id} options={options} />
          )),
        )}
      </BottomScrollListener>

      {isFetchingNextPage && <Spinner className="m-4" />}

      {hasFetchedAllPages && (
        <div className="text-sm text-center text-gray-500 mt-2">No more {collection} to show</div>
      )}
    </>
  );
}

export default PaymentsCharges;
